// this is a class that is used to help parse lead-id patterns and do general regex


// ==========================================
// LEAD ID PATTERNER CLASS
// ==========================================

export class LeadIdPatterner {

    /**
     * Parse a POSSIBLE leadId and return it in its NORMALIZED form, or return NULL if it does not appear to be a valid lead.
     * Handles three styles dynamically — no configuration table required:
     *  - NY Noir          "2-8920"        → "2-8920"
     *  - SHCD/Mythos/BOI, letters-first   "NW12", "NW-12b"  → "12NW"
     *  - SHCD/Mythos/BOI, digits-first    "12NW", "12b NW"  → "12NW"
     * @param {string} leadId
     * @returns {string|null} Canonical lead ID, or null if the input doesn't look like a lead.
     */
    normalizeLeadId(leadId) {
        if (!leadId || typeof leadId !== "string") return null;
        const s = leadId.trim();
        if (!s) return null;

        // NY Noir: digits-dash-digits
        let m = s.match(/^(\d+)\s*-\s*(\d+)$/);
        if (m) return `${m[1]}-${m[2]}`;

        // SHCD / Mythos / BOI, letters-first
        m = s.match(/^([A-Za-z]+)[\s\-:]*(\d+[A-Za-z]?)$/);
        if (m) return (m[2] + m[1]).toUpperCase();

        // SHCD / Mythos / BOI, digits-first
        m = s.match(/^(\d+[A-Za-z]?)[\s\-:]*([A-Za-z]+)$/);
        if (m) return (m[1] + m[2]).toUpperCase();

        return null;
    }


    /**
     * Find all lead-ID occurrences within a line of text and return their canonical forms.
     * Uses the same three styles as {@link normalizeLeadId} with whitespace lookarounds
     * for word boundaries.
     * @param {string} line
     * @returns {string[]} Deduped canonical lead IDs in encounter order.
     */
    findAllIdPatternsInLine(line) {
        const idList = [];
        if (!line || typeof line !== "string") return idList;

        const styles = [
            [/(?<=\s|^)(\d+)\s*-\s*(\d+)(?=\s|$)/g, (m) => `${m[1]}-${m[2]}`],
            [/(?<=\s|^)([A-Za-z]+)[\s\-:]*(\d+[A-Za-z]?)(?=\s|$)/g, (m) => (m[2] + m[1]).toUpperCase()],
            [/(?<=\s|^)(\d+[A-Za-z]?)[\s\-:]*([A-Za-z]+)(?=\s|$)/g, (m) => (m[1] + m[2]).toUpperCase()],
        ];

        for (const [pattern, toCanonical] of styles) {
            pattern.lastIndex = 0;
            let m;
            while ((m = pattern.exec(line)) !== null) {
                const norm = toCanonical(m);
                if (norm && !idList.includes(norm)) {
                    idList.push(norm);
                }
            }
        }
        return idList;
    }
}



// ==========================================
// SINGLETON
// ==========================================

let defaultLeadIdPatterner = null;

/**
 * Get (or create) the default singleton LeadIdPatterner instance.
 * @returns {LeadIdPatterner}
 */
export function getDefaultLeadIdPatterner() {
    if (!defaultLeadIdPatterner) {
        defaultLeadIdPatterner = new LeadIdPatterner();
    }
    return defaultLeadIdPatterner;
}
